import type { CrewPhase, DispatchCrewMember } from '../types/dispatch';
import type { CardPhase } from './dispatch-phase-cards';

/**
 * Which hands a dispatch card shows, and what a crew drop on the board writes
 * (API #730, `job_crew_assignments.phase`).
 *
 * ─── THE BUG THIS CLOSES ────────────────────────────────────────────────────
 *
 * TNT: a hand assigned to the run (tong) card was also on the wash card, and
 * got the wash call sheet. The column existed; the board never read it and
 * never wrote it, so every assignment was NULL — and NULL is every phase.
 *
 * ─── THE VOCABULARY ─────────────────────────────────────────────────────────
 *
 *   'wash' | 'run' | 'both' | null      null = not stated = EVERY phase
 *
 * The same reading the API's call sheet uses (crewForPhase): a card for phase
 * P shows NULL, 'both' and P. NULL is not "no phase" — every production row was
 * NULL when #730 shipped, so reading it that way would empty every card.
 *
 * ─── ONE CARD OR TWO ────────────────────────────────────────────────────────
 *
 * The split applies only when the job draws a wash card of its own
 * (`PhaseCard.crewSplit`). A job with one card shows its whole crew on it, and
 * a drop there writes no phase: stating 'run' on a job whose wash shares the
 * run's day would take the hand off a wash call sheet for no reason anyone can
 * see on the board.
 *
 * ─── THE DROP RULES ─────────────────────────────────────────────────────────
 *
 * Onto a card (from the CREW column, or a chip from another card):
 *   not on the job              → add, phase = this card (split) / unstated
 *   already shown on this card  → nothing, EXCEPT:
 *     NULL, from elsewhere      → this phase only (how a dispatcher splits an
 *                                 existing crew)
 *   on the job, the other phase → 'both'
 *
 * Off a card (onto the CREW column, or a chip moved to another job):
 *   'both'                      → the other phase, row kept
 *   NULL (split)                → the other phase, row kept
 *   this phase, or one card     → removed
 *
 * A chip picked up and put back on the card it came from is not a drop, and
 * writes nothing even for a NULL hand. "The card it came from" is the job and
 * phase, not the day: a carried-over wash's Day 2 is the same wash.
 */

export type CrewPhaseValue = CrewPhase | null;

export const CREW_PHASES: readonly CrewPhase[] = ['wash', 'run', 'both'];

/**
 * Every value, NULL included. NULL reads as Both because it behaves as both
 * everywhere it is read; a screen that shows it says separately that it was
 * never set.
 */
export const CREW_PHASE_LABEL: Record<CrewPhase | 'null', string> = {
  wash: 'Wash',
  run: 'Run',
  both: 'Both',
  null: 'Both',
};

/** The phase a hand carries, with an absent key read as NULL. */
export function phaseOf(member: Pick<DispatchCrewMember, 'phase'>): CrewPhaseValue {
  const p = member.phase;
  return p === 'wash' || p === 'run' || p === 'both' ? p : null;
}

const otherPhase = (p: CardPhase): CardPhase => (p === 'wash' ? 'run' : 'wash');

export interface CrewCard {
  jobId: string;
  cardType: CardPhase;
  /** The job draws a separate wash card — see PhaseCard.crewSplit. */
  split: boolean;
}

export function showsOnCard(phase: CrewPhaseValue, card: Pick<CrewCard, 'cardType' | 'split'>): boolean {
  if (!card.split) return true;
  return phase === null || phase === 'both' || phase === card.cardType;
}

/**
 * The hands one card shows. `cardType` is undefined for the Pending and Status
 * boards, which draw a job once — everyone.
 */
export function crewForCard(
  crew: DispatchCrewMember[],
  cardType: CardPhase | undefined,
  split: boolean,
): DispatchCrewMember[] {
  if (!cardType || !split) return crew;
  return crew.filter((m) => showsOnCard(phaseOf(m), { cardType, split }));
}

export interface CrewPatchBody {
  add?: (string | { userId: string; phase: CrewPhaseValue })[];
  remove?: string[];
  phases?: { userId: string; phase: CrewPhaseValue }[];
  /**
   * API 0426. The hand's position on the job: `phase` null = the whole job,
   * `positionId` null clears. A hand is named whole-job OR per phase in one
   * request, never both (the API 400s). Applied after add/remove, so a hand
   * added in the same request can be positioned in it.
   */
  positions?: { userId: string; phase: 'wash' | 'run' | null; positionId: string | null }[];
}

export type CrewPlan =
  | { kind: 'none'; reason: 'already-shown' | 'same-card' | 'not-shown' }
  | { kind: 'add'; body: CrewPatchBody; phase: CrewPhaseValue }
  | { kind: 'set-phase'; body: CrewPatchBody; phase: CrewPhase }
  | { kind: 'remove'; body: CrewPatchBody };

const setPhase = (userId: string, phase: CrewPhase): CrewPlan => ({
  kind: 'set-phase',
  body: { phases: [{ userId, phase }] },
  phase,
});

/**
 * A hand dropped onto `target`.
 *
 * @param targetCrew the target job's FULL crew, not the card's shown subset —
 *   a 'wash' hand is not shown on the run card and is still on the job.
 * @param fromSameCard the chip was picked up from this job's card of this phase.
 */
export function planDropOnCard(args: {
  userId: string;
  targetCrew: DispatchCrewMember[];
  target: CrewCard;
  fromSameCard: boolean;
}): CrewPlan {
  const { userId, targetCrew, target, fromSameCard } = args;
  const existing = targetCrew.find((m) => m.id === userId);

  if (!existing) {
    if (!target.split) {
      // A bare id: the row takes the column's NULL default.
      return { kind: 'add', body: { add: [userId] }, phase: null };
    }
    return { kind: 'add', body: { add: [{ userId, phase: target.cardType }] }, phase: target.cardType };
  }

  if (fromSameCard) return { kind: 'none', reason: 'same-card' };
  if (!target.split) return { kind: 'none', reason: 'already-shown' };

  const phase = phaseOf(existing);
  if (phase === null) return setPhase(userId, target.cardType);
  if (phase === 'both' || phase === target.cardType) return { kind: 'none', reason: 'already-shown' };
  return setPhase(userId, 'both');
}

/** A hand dragged off `source` — onto the CREW column, or to another job. */
export function planDragOffCard(args: {
  member: DispatchCrewMember;
  source: Pick<CrewCard, 'cardType' | 'split'>;
}): CrewPlan {
  const { member, source } = args;
  const remove: CrewPlan = { kind: 'remove', body: { remove: [member.id] } };
  if (!source.split) return remove;

  const phase = phaseOf(member);
  if (phase === null || phase === 'both') return setPhase(member.id, otherPhase(source.cardType));
  if (phase === source.cardType) return remove;
  // The other phase's hand is not shown on this card, so it cannot have been
  // dragged from it. A stale board is the only way here; write nothing.
  return { kind: 'none', reason: 'not-shown' };
}

/**
 * The crew a PATCH /dispatch/jobs/:id/crew response describes, laid over what
 * the board already holds.
 *
 * The response is the database's crew after the write — the source of truth
 * for membership and `phase`. It does NOT carry `specialty` (the board read
 * does), so each row keeps the specialty the board had for that hand, or the
 * one `known` supplies for a hand just added from the CREW column. Rows the
 * response omits are gone.
 */
export function mergeCrewResponse(
  current: DispatchCrewMember[],
  response: unknown,
  known: DispatchCrewMember[] = [],
): DispatchCrewMember[] | null {
  if (!Array.isArray(response)) return null;
  const byId = new Map<string, DispatchCrewMember>();
  for (const m of known) byId.set(m.id, m);
  for (const m of current) byId.set(m.id, m);
  const merged: DispatchCrewMember[] = [];
  for (const raw of response) {
    if (!raw || typeof raw !== 'object' || typeof (raw as any).id !== 'string') continue;
    const row = raw as DispatchCrewMember;
    const prev = byId.get(row.id);
    merged.push({
      ...prev,
      ...row,
      specialty: row.specialty !== undefined ? row.specialty : (prev?.specialty ?? null),
      phase: phaseOf(row),
    });
  }
  return merged;
}

/** "on the wash only" / "on wash and run" — the toast after a phase write. */
export function describeCrewPhase(phase: CrewPhase): string {
  return phase === 'both' ? 'on wash and run' : `on the ${phase} only`;
}

/** What a crew chip carries in its drag data. */
export interface CrewChipDragData {
  type: 'crew';
  userId: string;
  sourceJobId: string;
  crewMember: DispatchCrewMember;
  /** Undefined on the Pending and Status boards. */
  sourceCardType?: CardPhase;
  sourceSplit: boolean;
}

/** What a job card registers as a crew drop target. */
export interface JobCardDropData {
  type: 'jobcard';
  jobId: string;
  cardType?: CardPhase;
  split: boolean;
}
